import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const YEARS_OPTIONS = [
  "Less than 1 year",
  "1–3 years",
  "4–7 years",
  "8–15 years",
  "16–25 years",
  "More than 25 years",
];

const INITIAL = {
  full_name: "",
  title: "",
  email: "",
  church_name: "",
  ministerial_affiliation: "",
  district_name: "",
  superintendent_name: "",
  bishop_name: "",
  denomination_name: "",
  years_in_ministry: "",
  expectations: "",
};

// Fields that are always required regardless of affiliation
const BASE_REQUIRED = [
  "full_name",
  "title",
  "email",
  "church_name",
  "ministerial_affiliation",
  "years_in_ministry",
  "expectations",
];

export default function RegistrationForm({ onSubmit }) {
  const [values, setValues] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    const required = [...BASE_REQUIRED];
    if (values.ministerial_affiliation === "COGIC") {
      required.push("district_name", "superintendent_name");
    } else if (values.ministerial_affiliation === "Other") {
      required.push("bishop_name", "denomination_name");
    }
    for (const key of required) {
      if (!String(values[key] || "").trim()) {
        next[key] = "Required";
      }
    }
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      next.email = "Please enter a valid email address.";
    }
    if (values.expectations && values.expectations.trim().length < 10) {
      next.expectations = "Please share a little more (10+ characters).";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete all required fields", {
        description: "Every field marked with * must be filled in before submitting.",
      });
      // Scroll to first error
      requestAnimationFrame(() => {
        const firstErr = document.querySelector('[data-testid^="error-"]');
        if (firstErr) firstErr.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(values);
      setValues(INITIAL);
    } catch {
      /* toast handled upstream */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="registration-form"
      className="bg-white border border-[#EBE8E3] p-8 md:p-12 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)]"
      noValidate
    >
      <div className="flex items-center gap-3 mb-8">
        <span className="gold-rule" />
        <span className="eyebrow" style={{ color: "#6B1F2A" }}>
          Applicant Details
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
        <Field
          id="full_name"
          label="Full Name"
          value={values.full_name}
          onChange={setField("full_name")}
          error={errors.full_name}
          placeholder="Min. John Doe"
          testid="input-full-name"
        />
        <Field
          id="title"
          label="Title"
          value={values.title}
          onChange={setField("title")}
          error={errors.title}
          placeholder="Senior Pastor"
          testid="input-title"
        />
        <Field
          id="email"
          label="Email"
          type="email"
          value={values.email}
          onChange={setField("email")}
          error={errors.email}
          placeholder="you@example.com"
          testid="input-email"
        />
        <Field
          id="church_name"
          label="Church Name"
          value={values.church_name}
          onChange={setField("church_name")}
          error={errors.church_name}
          placeholder="New Mount Joy Church"
          testid="input-church-name"
        />
        <SelectField
          id="years_in_ministry"
          label="Years in Ministry"
          value={values.years_in_ministry}
          onChange={setField("years_in_ministry")}
          error={errors.years_in_ministry}
          options={YEARS_OPTIONS}
          testid="input-years-in-ministry"
        />
      </div>

      {/* Ministerial Affiliation */}
      <div className="mt-8">
        <span className="field-label">
          Ministerial Affiliation
          <span className="text-red-600 ml-1" aria-hidden="true">*</span>
        </span>
        <div
          role="radiogroup"
          aria-label="Ministerial Affiliation"
          className="mt-3 flex flex-col sm:flex-row gap-3"
        >
          <AffiliationOption
            id="aff-cogic"
            testid="affiliation-cogic"
            label="Church of God in Christ (COGIC)"
            checked={values.ministerial_affiliation === "COGIC"}
            onChange={() => {
              setValues((v) => ({
                ...v,
                ministerial_affiliation: "COGIC",
                bishop_name: "",
                denomination_name: "",
              }));
              setErrors((er) => ({
                ...er,
                ministerial_affiliation: undefined,
                bishop_name: undefined,
                denomination_name: undefined,
              }));
            }}
          />
          <AffiliationOption
            id="aff-other"
            testid="affiliation-other"
            label="Other Denomination/Fellowship"
            checked={values.ministerial_affiliation === "Other"}
            onChange={() => {
              setValues((v) => ({
                ...v,
                ministerial_affiliation: "Other",
                district_name: "",
                superintendent_name: "",
              }));
              setErrors((er) => ({
                ...er,
                ministerial_affiliation: undefined,
                district_name: undefined,
                superintendent_name: undefined,
              }));
            }}
          />
        </div>
        {errors.ministerial_affiliation && (
          <p
            data-testid="error-ministerial-affiliation"
            className="mt-2 text-xs text-[#6B1F2A]"
          >
            {errors.ministerial_affiliation}
          </p>
        )}
      </div>

      {/* Conditional affiliation fields */}
      {values.ministerial_affiliation === "COGIC" && (
        <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
          <Field
            id="district_name"
            label="District Name"
            value={values.district_name}
            onChange={setField("district_name")}
            error={errors.district_name}
            placeholder="Eastern District"
            testid="input-district-name"
          />
          <Field
            id="superintendent_name"
            label="Superintendent's Name"
            value={values.superintendent_name}
            onChange={setField("superintendent_name")}
            error={errors.superintendent_name}
            placeholder="Bishop John Smith"
            testid="input-superintendent-name"
          />
        </div>
      )}

      {values.ministerial_affiliation === "Other" && (
        <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
          <Field
            id="denomination_name"
            label="Denomination / Fellowship"
            value={values.denomination_name}
            onChange={setField("denomination_name")}
            error={errors.denomination_name}
            placeholder="Full Gospel Baptist Fellowship"
            testid="input-denomination-name"
          />
          <Field
            id="bishop_name"
            label="Bishop's Name"
            value={values.bishop_name}
            onChange={setField("bishop_name")}
            error={errors.bishop_name}
            placeholder="Bishop Jane Smith"
            testid="input-bishop-name"
          />
        </div>
      )}

      <div className="mt-8">
        <label htmlFor="expectations" className="field-label">
          What do you expect to gain from the Shepherd&rsquo;s Academy?
          <span className="text-red-600 ml-1" aria-hidden="true">*</span>
        </label>
        <textarea
          id="expectations"
          data-testid="input-expectations"
          value={values.expectations}
          onChange={setField("expectations")}
          placeholder="Share your hopes for this season of formation…"
          className="field-input field-input--textarea"
          rows={5}
        />
        {errors.expectations && (
          <p
            data-testid="error-expectations"
            className="mt-2 text-xs text-[#6B1F2A]"
          >
            {errors.expectations}
          </p>
        )}
      </div>

      <div className="mt-10 flex flex-col-reverse md:flex-row items-stretch md:items-center justify-between gap-5">
        <p className="text-xs text-[#6E6869] max-w-md leading-relaxed">
          By submitting, you confirm your superintendent has been notified of
          your interest. We will only use this information to process your
          registration.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="registration-submit-button"
          className="btn-primary w-full md:w-auto"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting
            </>
          ) : (
            <>Submit Registration</>
          )}
        </button>
      </div>
    </form>
  );
}

function Field({ id, label, value, onChange, error, placeholder, testid, type = "text" }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
        <span className="text-red-600 ml-1" aria-hidden="true">*</span>
      </label>
      <input
        id={id}
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="field-input"
        autoComplete="off"
        required
      />
      {error && (
        <p
          data-testid={`error-${id.replace(/_/g, "-")}`}
          className="mt-2 text-xs text-[#6B1F2A]"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function SelectField({ id, label, value, onChange, error, options, testid }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
        <span className="text-red-600 ml-1" aria-hidden="true">*</span>
      </label>
      <select
        id={id}
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="field-input"
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='%236B1F2A' stroke-width='1.5' stroke-linecap='round'/></svg>\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 4px center",
          paddingRight: "24px",
          color: value ? "#1A1516" : "#C8C2C3",
        }}
      >
        <option value="" disabled>
          Select a range…
        </option>
        {options.map((opt) => (
          <option key={opt} value={opt} style={{ color: "#1A1516" }}>
            {opt}
          </option>
        ))}
      </select>
      {error && (
        <p
          data-testid={`error-${id.replace(/_/g, "-")}`}
          className="mt-2 text-xs text-[#6B1F2A]"
        >
          {error}
        </p>
      )}
    </div>
  );
}


function AffiliationOption({ id, testid, label, checked, onChange }) {
  return (
    <label
      htmlFor={id}
      data-testid={testid}
      className="group flex-1 cursor-pointer border bg-white px-4 py-3 flex items-center gap-3 transition-colors"
      style={{
        borderColor: checked ? "#6B1F2A" : "#EBE8E3",
        boxShadow: checked ? "inset 0 0 0 1px #6B1F2A" : "none",
      }}
    >
      <input
        id={id}
        type="radio"
        name="ministerial_affiliation"
        className="sr-only"
        checked={checked}
        onChange={onChange}
      />
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full border transition-colors"
        style={{
          borderColor: checked ? "#6B1F2A" : "#C8C2C3",
        }}
      >
        {checked && (
          <span
            className="block w-2.5 h-2.5 rounded-full"
            style={{ background: "#6B1F2A" }}
          />
        )}
      </span>
      <span
        className="text-sm md:text-[15px]"
        style={{
          color: checked ? "#1A1516" : "#3A3536",
          fontWeight: checked ? 600 : 400,
        }}
      >
        {label}
      </span>
    </label>
  );
}
